import { Component, OnInit } from '@angular/core';
import { Group, MicrosoftAuthenticatorAuthenticationMethodConfiguration } from '@microsoft/microsoft-graph-types-beta';
import { select, Store } from '@ngrx/store';
import { map, Observable, skipUntil } from 'rxjs';
import { BaseComponent } from '../../../base/base.component';
import { initAuthenticationMethodsMicrosoftAuthenticatorForm } from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/authentication-method-policy/microsoft-authenticator/form';
import {
    AuthenticationMethodPolicyMicrosoftAuthenticatorForm,
    microsoftAuthenticatorIncludeOptionsLookups,
} from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/authentication-method-policy/microsoft-authenticator/model';
import { updateAuthenticationMethodsMicrosoftAuthenticatorSchema } from 'projects/angular-clarity/src/app/modules/sway/tools/schemas/graph/authentication-method-policy/microsoft-authenticator/schema';
import { client } from 'projects/angular-clarity/src/app/stores/client';

const featureValues = {
    default: 'Microsoft managed',
    enabled: 'Enabled',
    disabled: 'Disabled',
};
@Component({
    selector: 'app-baseline',
    templateUrl: './baseline.component.html',
    styleUrls: ['./baseline.component.scss', '../../../styles/baseline-style.scss'],
})
export class BaselineComponent extends BaseComponent implements OnInit {
    groups$: Observable<Map<string, Group>>;
    graphGroups$: Observable<Group[]>;
    includeOptionsLookups = microsoftAuthenticatorIncludeOptionsLookups;
    featureValues = featureValues;
    data: MicrosoftAuthenticatorAuthenticationMethodConfiguration;
    error: string;

    constructor(private store: Store<any>) {
        super();
    }

    ngOnInit(): void {
        this.graphGroups$ = this.store
            .pipe(select(client(this.tenant_id).graph.groups.all))
            .pipe(skipUntil(this.store.pipe(select(client(this.tenant_id).graph.groups.loaded))));

        this.groups$ = this.graphGroups$.pipe(
            map((groups) => {
                return new Map(groups.map((group) => [group.id, group]));
            }),
        );

        const schema = JSON.parse(JSON.stringify(this.baseline.schema));
        this.form = initAuthenticationMethodsMicrosoftAuthenticatorForm(schema);

        this.form.valueChanges.subscribe((_) => {
            const form = this.formValue;
            const updatedSchema = updateAuthenticationMethodsMicrosoftAuthenticatorSchema(form);
            this.checkErrors(form);
            this.baselineChange.next({
                ...this.baseline,
                schema: JSON.parse(JSON.stringify(updatedSchema)),
            });
        });
    }

    checkErrors(form: AuthenticationMethodPolicyMicrosoftAuthenticatorForm): void {
        const excludedIds = form.excludeTargets;

        const featureChecks = [
            {
                inclusionId: form.featureSettings.companionAppAllowedState.includeTarget.id,
                exclusionId: form.featureSettings.companionAppAllowedState.excludeTarget.id,
                state: form.featureSettings.companionAppAllowedState.state,
                message:
                    'You cannot exclude group in excluded target and include/exclude the same group in companion applications',
            },
            {
                inclusionId: form.featureSettings.displayAppInformationRequiredState.includeTarget.id,
                exclusionId: form.featureSettings.displayAppInformationRequiredState.excludeTarget.id,
                state: form.featureSettings.displayAppInformationRequiredState.state,
                message:
                    'You cannot exclude group in excluded target and include/exclude the same group in display app information',
            },
            {
                inclusionId: form.featureSettings.displayLocationInformationRequiredState.includeTarget.id,
                exclusionId: form.featureSettings.displayLocationInformationRequiredState.excludeTarget.id,
                state: form.featureSettings.displayLocationInformationRequiredState.state,
                message:
                    'You cannot exclude group in excluded target and include/exclude the same group in display location information',
            },
        ];

        for (const check of featureChecks) {
            if (excludedIds.includes(check.inclusionId) && check.state !== 'disabled') {
                this.baselineErrorChange.next({ remediate: true, save: true });
                this.error = check.message;
                return;
            }
        }

        for (const check of featureChecks) {
            if (excludedIds.includes(check.exclusionId) && check.state !== 'disabled') {
                this.baselineErrorChange.next({ remediate: true, save: true });
                this.error = check.message;
                return;
            }
        }

        // No errors found
        this.baselineErrorChange.next({ remediate: false, save: false });
        this.error = '';
    }

    get formValue(): AuthenticationMethodPolicyMicrosoftAuthenticatorForm {
        return this.form.getRawValue();
    }
}
